import { useContext, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Text, Spinner, Buttons, FlexContainer, ModalContext } from '@taxdown/piccolo-web';
import { YayoyakiFormatter } from '@taxdown/kami-sama/dist/kamiSama/yayoyakiFormatter';
import { locizeNS } from 'Services/i18n';
import { history } from 'Store';
import { routeStateSelector } from 'Store/modules/router/selectors';
import { isAuthenticatedSelector } from 'Store/modules/auth/selectors';
import { useTranslation } from 'Hooks/useTranslation';
import { useGetPlanFlexInfo } from 'Containers/Result/hooks';
import { useFetchGetCustomers } from 'Containers/Plans/hooks';
import FlowColLayout from 'Components/Layouts/FlowColLayout';
import CustomTransition from 'Components/CustomTransition';
import Trans from 'Components/Trans';
import Reviews from 'Components/Reviews';
import SaveCardModal from 'Components/Modal/components/SaveCardModal';
import { routes } from 'Constants/routes';
import { FRONT_PLANS, plansNameMapping } from 'Constants/plans';
import PayLaterInfoContainer from '../PayLaterInfoContainer';
import * as Styled from './styles';

const PaymentLater = (): JSX.Element => {
  const { t } = useTranslation(locizeNS.RESULT);
  const { showModal } = useContext(ModalContext);
  const isAuthenticated = useSelector(isAuthenticatedSelector);
  const paymentLaterProps = useSelector(routeStateSelector);
  const { data, isLoading: loading } = useGetPlanFlexInfo();
  const { data: customer } = useFetchGetCustomers(isAuthenticated);

  useEffect(() => {
    if (
      !paymentLaterProps ||
      !paymentLaterProps.taxReturnType ||
      !paymentLaterProps.taxReturnResult
    ) {
      history.push(routes.RESULT);
    }
  }, [paymentLaterProps]);

  useEffect(() => {
    if (customer && plansNameMapping(customer?.activePlan?.name) !== FRONT_PLANS.BASIC) {
      history.push(routes.RESULT);
    }
  }, [customer]);

  const checkoutParams = {
    filing: 'true',
    plans: FRONT_PLANS.FLEX,
    submitFlex: 'true',
    saveCard: 'true',
  };

  const handleClick = (): void => {
    showModal({
      comp: (
        <SaveCardModal
          successUrl={routes.CHECKOUT_SUCCESS_STEP}
          cancelUrl={routes.RESULT}
          checkoutParams={checkoutParams}
        />
      ),
    });
  };

  return (
    <FlowColLayout section={paymentLaterProps?.section} isFlow>
      <CustomTransition start>
        {!data || loading ? (
          <FlexContainer fullHeight justify="center" align="center">
            <Spinner size="M" justify="center" align="center" />
          </FlexContainer>
        ) : (
          <Styled.Container
            direction="column"
            align="center"
            justify="center"
            gap="dimension_24"
            fullHeight
          >
            <FlexContainer direction="column" gap="dimension_14">
              <Text type="paragraphs" color="dark" size="XL_XL_S">
                {t('Result.payLater.title', {
                  context: !paymentLaterProps?.isFlexNotMandatory && 'notMandatory',
                })}
              </Text>
              {paymentLaterProps?.isFlexNotMandatory && (
                <FlexContainer align="center" gap="dimension_08">
                  <Text type="paragraphs" color="dark" size="L_L_S">
                    {t('Result.payLater.subtitle')}
                  </Text>
                  <Text type="paragraphs" color="dark" size="L_XL_S">
                    {YayoyakiFormatter.formatAmountMxn(
                      Number(Math.abs(paymentLaterProps.taxReturnResult))
                    )}
                  </Text>
                </FlexContainer>
              )}
            </FlexContainer>
            {data && <PayLaterInfoContainer data={data} />}
            <Styled.ButtonsContainer direction="column" fullWidth gap="dimension_24">
              <Buttons.Classic
                testID="payLater"
                mode="live"
                size="m"
                onClick={() => handleClick()}
                label={t('Result.CTA.paylater')}
              />
              <Text type="paragraphs" size="XS_S_S" color="grey" align="center">
                <Trans
                  i18nKey="Result.paylater.conditions"
                  ns="result"
                  components={{
                    a: <Styled.Link target="_blank" />,
                  }}
                />
              </Text>
              <Reviews />
            </Styled.ButtonsContainer>
          </Styled.Container>
        )}
      </CustomTransition>
    </FlowColLayout>
  );
};

export default PaymentLater;
